/**
 * TopPerformingFundraisers.tsx - Top Performers Showcase Component
 *
 * This component displays the top-performing fundraisers in a highlighted
 * section with key metrics and quick action buttons.
 *
 * Key Features:
 * - Top 3 performing fundraisers display
 * - Performance metrics and progress indicators
 * - Quick action buttons for management
 * - Expiration badges and status indicators
 * - Responsive card layout with hover effects
 * - Empty state handling for no fundraisers
 *
 * Performance Metrics:
 * - Total amount raised with goal progress
 * - Percentage completion with visual progress bar
 * - Status indicators and expiration warnings
 * - Last activity and creation date information
 *
 * Quick Actions:
 * - View Details: Navigate to fundraiser details
 * - Edit: Open in builder for modifications
 * - Preview: View live fundraiser page
 *
 * Props:
 * - topPerformingFundraisers: Fundraiser[] - Array of top performers
 * - onViewDetails: (fundraiser: Fundraiser) => void - View details handler
 * - onEdit: (fundraiser: Fundraiser) => void - Edit handler
 * - onPreview: (fundraiser: Fundraiser) => void - Preview handler
 *
 * Dependencies: Fundraiser types, badge utilities, styled-components
 */

import React, { useState, useEffect } from "react";
import styled from "styled-components";
import { Eye, Pen, Link, TrendingUp, Heart } from "lucide-react";
import {
  shouldShowExpirationBadge,
  getExpirationBadgeText,
} from "../../utils/badgesUtils";
import { TopFundRaiser } from "../../models";

const Section = styled.div`
  margin-bottom: 40px;
`;

const SectionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
`;

const SectionTitle = styled.h2`
  font-size: 24px;
  font-weight: 700;
  color: #1e293b;
`;

const TopFundraisersGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 24px;
  margin-bottom: 32px;

  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
  }
`;

const PlaceholderContainer = styled.div`
  background: #ffffff;
  border-radius: 16px;
  padding: 48px 24px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  border: 1px solid #f1f5f9;
  text-align: center;
  color: #64748b;
  min-height: 200px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
`;

const PlaceholderIcon = styled.div`
  width: 80px;
  height: 80px;
  border-radius: 50%;
  background: #f1f5f9;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 20px;
  color: #94a3b8;
`;

const PlaceholderTitle = styled.h4`
  font-size: 18px;
  font-weight: 600;
  color: #1e293b;
  margin: 0 0 8px 0;
`;

const PlaceholderText = styled.p`
  font-size: 14px;
  color: #64748b;
  margin: 0;
  line-height: 1.5;
  max-width: 300px;
`;

const TopFundraiserCard = styled.div`
  position: relative;
  background: #ffffff;
  border-radius: 16px;
  padding: 24px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  transition: transform 0.2s ease;
  cursor: pointer;

  &:hover {
    transform: translateY(-2px);

    .hover-overlay {
      opacity: 1;
      visibility: visible;
    }
  }
`;

const HoverOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  backdrop-filter: blur(8px);
  border-radius: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  visibility: hidden;
  transition: all 0.2s ease;
  z-index: 10;
`;

const HoverActions = styled.div`
  display: flex;
  flex-direction: row;
  gap: 12px;
`;

const HoverButton = styled.button`
  width: 44px;
  height: 44px;
  border-radius: 50%;
  border: none;
  background: rgba(255, 255, 255, 0.2);
  backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
  color: #ffffff;

  &:hover {
    background: rgba(255, 255, 255, 0.3);
    color: #ffffff;
    transform: scale(1.05);
  }

  &:active {
    transform: scale(0.95);
  }
`;

const FundraiserImage = styled.img`
  width: 100%;
  height: 180px;
  object-fit: cover;
  border-radius: 12px;
  margin-bottom: 16px;
`;

const CardImageContainer = styled.div`
  position: relative;
  width: 100%;
  margin-bottom: 16px;
`;

const ExpirationBadge = styled.div`
  position: absolute;
  top: 12px;
  right: 12px;
  background: rgba(220, 38, 38, 0.95);
  color: #ffffff;
  font-size: 10px;
  font-weight: 700;
  padding: 6px 8px;
  border-radius: 6px;
  border: 1px solid rgba(255, 255, 255, 0.3);
  backdrop-filter: blur(8px);
  white-space: nowrap;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  box-shadow: 0 2px 8px rgba(220, 38, 38, 0.3);
`;

const FundraiserName = styled.h4`
  font-size: 18px;
  font-weight: 700;
  color: #1e293b;
  margin-bottom: 8px;
`;

const FundraiserProgress = styled.div`
  margin-bottom: 12px;
`;

const ProgressBar = styled.div`
  width: 100%;
  height: 8px;
  background: #e2e8f0;
  border-radius: 4px;
  overflow: hidden;
  margin-bottom: 8px;
`;

const ProgressFill = styled.div<{ percentage: number }>`
  width: ${(props) => props.percentage}%;
  height: 100%;
  background: linear-gradient(135deg, #10b981, #059669);
  border-radius: 4px;
  transition: width 0.8s cubic-bezier(0.4, 0, 0.2, 1);
`;

const ProgressAmount = styled.span`
  color: #4b5563;
`;

const ProgressText = styled.div`
  display: flex;
  justify-content: space-between;
  font-size: 14px;
  color: #64748b;
`;

interface TopPerformingFundraisersProps {
  onViewDetails: (fundraiserId: string) => void;
  onEdit: (fundraiserId: string) => void;
  onPreview: (fundraiserId: string) => void;
  data: TopFundRaiser[];
}

export const TopPerformingFundraisers: React.FC<
  TopPerformingFundraisersProps
> = ({ onViewDetails, onEdit, onPreview, data }) => {
  //const fundraisers = topPerformingFundraisers || [];

  // const [displayedPercentages, setDisplayedPercentages] = useState<Record<string, number>>({});

  // Trigger animations after component mounts
  // useEffect(() => {
  //   const timer = setTimeout(() => {
  //     const newPercentages: Record<string, number> = {};
  //     fundraisers.forEach(fundraiser => {
  //       const actualProgress = (fundraiser.currentAmount / fundraiser.goal) * 100;
  //       const displayProgress = actualProgress;
  //       const barProgress = Math.min(actualProgress, 100);
  //       newPercentages[fundraiser.id] = barProgress;
  //     });
  //     setDisplayedPercentages(newPercentages);
  //   }, 100);

  //   return () => clearTimeout(timer);
  // }, [fundraisers]);

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  if (data == null) return null;
  return (
    <Section>
      <SectionHeader>
        <SectionTitle>Top Fundraisers</SectionTitle>
      </SectionHeader>

      {data.length === 0 ? (
        <PlaceholderContainer>
          <PlaceholderIcon>
            <TrendingUp size={40} />
          </PlaceholderIcon>
          <PlaceholderTitle>
            No Active or Completed Fundraisers
          </PlaceholderTitle>
          <PlaceholderText>
            Top performing fundraisers will appear here when you have active or
            completed fundraisers with donations.
          </PlaceholderText>
        </PlaceholderContainer>
      ) : (
        <TopFundraisersGrid>
          {data?.map((fundraiser) => {
            const actualProgress =
              (fundraiser.totalRaised / fundraiser.goalAmount) * 100;
            const displayProgress = actualProgress;
            // const barProgress = Math.min(actualProgress, 100);
            // const animatedPercentage = displayedPercentages[fundraiser.id] || 0;

            // Get the primary image from mediaItems or fallback to image property
            // const primaryImage = fundraiser.mediaItems && fundraiser.mediaItems.length > 0
            //   ? fundraiser.mediaItems[0].src
            //   : fundraiser.image;

            return (
              <TopFundraiserCard
                key={fundraiser.id}
                onClick={() => onViewDetails(fundraiser.id)}
              >
                <HoverOverlay className="hover-overlay">
                  <HoverActions>
                    <HoverButton
                      onClick={(e) => {
                        e.stopPropagation();
                        onViewDetails(fundraiser.id);
                      }}
                      title="Details"
                    >
                      <Eye size={20} />
                    </HoverButton>
                    <HoverButton
                      onClick={(e) => {
                        e.stopPropagation();
                        onEdit(fundraiser.id);
                      }}
                      title="Edit"
                    >
                      <Pen size={20} />
                    </HoverButton>
                    <HoverButton
                      onClick={(e) => {
                        e.stopPropagation();
                        onPreview(fundraiser.id);
                      }}
                      title="Donate"
                    >
                      <Heart size={20} />
                    </HoverButton>
                  </HoverActions>
                </HoverOverlay>
                <CardImageContainer>
                  {/* <https://images.pexels.com/photos/1105666/pexels-photo-1105666.jpeg?w=400&h=300&fit=crop> */}
                  {fundraiser.getImageToShow() && (
                    <FundraiserImage
                      src={fundraiser.getImageToShow()}
                      alt={fundraiser.name}
                      // onError={(e) => {
                      //   const target = e.target as HTMLImageElement;
                      //   target.src = 'https://images.pexels.com/photos/1105666/pexels-photo-1105666.jpeg?w=400&h=300&fit=crop';
                      // }}
                    />
                  )}
                  {shouldShowExpirationBadge(
                    fundraiser.endDate || "",
                    fundraiser.status,
                  ) &&
                    fundraiser.endDate && (
                      <ExpirationBadge>
                        {getExpirationBadgeText(new Date(fundraiser.endDate))}
                      </ExpirationBadge>
                    )}
                </CardImageContainer>
                <FundraiserName>{fundraiser.name}</FundraiserName>
                <FundraiserProgress>
                  <ProgressBar>
                    <ProgressFill percentage={fundraiser.percentage} />
                  </ProgressBar>
                  <ProgressText>
                    <span>
                      {fundraiser.goalAmount > 0
                        ? `${displayProgress.toFixed(1)}% of goal`
                        : "Raised to date"}
                    </span>
                    <ProgressAmount>
                      {formatCurrency(fundraiser.totalRaised)}
                    </ProgressAmount>
                  </ProgressText>
                </FundraiserProgress>
              </TopFundraiserCard>
            );
          })}
        </TopFundraisersGrid>
      )}
    </Section>
  );
};
